// btc15/GlossaryContent.js — the body of the "what does this mean?" sheet.
//
// Rendered identically on the phone and the desktop. Neither tab owns the
// copy: both pass a small palette and this draws the same explanations, so
// the two can never drift into describing the same number differently.
//
// palette: { text, muted, faint, border, surface, accent, green, red }
import { useEffect, useMemo, useRef, useState } from "react";
import { GROUPS, BY_ID, inGroup, searchGlossary, workedFor, spotFor, indicatorFor } from "./glossary";
import GlossaryArt from "./glossaryArt";
import TabMap, { RelationFlow } from "./TabMap";
import { HOWTO } from "./howto";
import { INDICATORS } from "./indicators";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

export function InfoDot({ onClick, palette, size = 14, testId, label = "What does this mean?" }) {
  const P = palette || {};
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      title={label}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size + 6, height: size + 6, minWidth: size + 6, padding: 0, marginLeft: 4,
        borderRadius: "50%", cursor: "pointer", verticalAlign: "middle",
        border: `1px solid ${P.border || "currentColor"}`, background: "transparent",
        color: P.faint || "currentColor", fontSize: size - 3, fontWeight: 800,
        lineHeight: 1, fontFamily: "inherit", flexShrink: 0,
      }}
    >
      i
    </button>
  );
}

function Paragraphs({ text, color, size = 12.5 }) {
  // the copy uses blank lines for paragraphs and "• " for bullets; both are
  // authored in glossary.js as plain text so the content stays readable there
  return (
    <>
      {String(text).split("\n\n").map((para, i) => (
        <p key={i} style={{ margin: i === 0 ? "0 0 8px" : "0 0 8px", fontSize: size, lineHeight: 1.55, color }}>
          {para.split("\n").map((line, j) => (
            <span key={j} style={{ display: line.startsWith("•") ? "block" : "inline", paddingLeft: line.startsWith("•") ? 2 : 0 }}>
              {line}{j < para.split("\n").length - 1 && !line.startsWith("•") ? " " : ""}
            </span>
          ))}
        </p>
      ))}
    </>
  );
}

function Entry({ entry, palette, ctx, open, onToggle, onPick }) {
  const P = palette;
  const worked = useMemo(() => (open ? workedFor(entry, ctx) : null), [open, entry, ctx]);
  const Indicator = INDICATORS[indicatorFor(entry.id)] || null;
  return (
    <div data-testid={`btc15-glossary-entry-${entry.id}`} style={{ borderBottom: `1px solid ${P.border}` }}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`btc15-glossary-toggle-${entry.id}`}
        style={{
          display: "flex", width: "100%", gap: 8, alignItems: "baseline", textAlign: "left",
          background: "transparent", border: 0, padding: "10px 0", cursor: "pointer", color: P.text, fontFamily: "inherit",
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: P.text }}>{entry.term}</span>
          {!open && <span style={{ display: "block", fontSize: 11.5, color: P.muted, marginTop: 2, lineHeight: 1.45 }}>{entry.short}</span>}
        </span>
        <span aria-hidden style={{ fontSize: 15, color: P.faint, lineHeight: 1 }}>{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 0 12px" }}>
          <div style={{ fontSize: 12, color: P.muted, fontStyle: "italic", marginBottom: 8, lineHeight: 1.5 }}>{entry.short}</div>
          {/* Where it lives, first — a number you cannot find on the tab is
              not yet explained. Then the REAL indicator if this term has
              one, so the picture is the widget rather than a drawing of it;
              otherwise the schematic. */}
          <TabMap spot={spotFor(entry.id)} palette={P} />
          {Indicator ? (
            <div data-testid={`btc15-glossary-live-${entry.id}`} style={{ margin: "2px 0 10px" }}>
              <Indicator palette={P} {...ctx} yesAsk={ctx?.yesAsk} compact />
              <div style={{ fontSize: 10, color: P.faint, marginTop: 3 }}>
                This is the live indicator from the tab, not an illustration of it.
              </div>
            </div>
          ) : (
            <GlossaryArt entry={entry} palette={P} ctx={ctx} />
          )}
          <RelationFlow id={entry.id} palette={P} onPick={onPick} />
          <Paragraphs text={entry.detail} color={P.text} />

          {entry.formula && (
            <div data-testid={`btc15-glossary-formula-${entry.id}`} style={{
              fontFamily: MONO, fontSize: 11.5, color: P.text, background: P.surface, whiteSpace: "pre-wrap",
              border: `1px solid ${P.border}`, borderRadius: 8, padding: "8px 10px", margin: "2px 0 8px", lineHeight: 1.5,
            }}>
              {entry.formula}
            </div>
          )}

          {worked && (
            <div data-testid={`btc15-glossary-worked-${entry.id}`} style={{
              fontSize: 11.5, color: P.text, background: `${P.accent}14`, border: `1px solid ${P.accent}44`,
              borderRadius: 8, padding: "8px 10px", marginBottom: 8, lineHeight: 1.5,
            }}>
              <span style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: P.accent, display: "block", marginBottom: 3 }}>
                Right now
              </span>
              <span style={{ fontFamily: MONO }}>{worked}</span>
            </div>
          )}

          {(entry.seeAlso || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 10.5, color: P.faint }}>See also</span>
              {entry.seeAlso.filter((id) => BY_ID[id]).map((id) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`btc15-glossary-link-${id}`}
                  onClick={() => onPick(id)}
                  style={{
                    fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 999, cursor: "pointer",
                    border: `1px solid ${P.border}`, background: "transparent", color: P.accent, fontFamily: "inherit",
                  }}
                >
                  {BY_ID[id].term}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── how to use: the screen, top to bottom ─────────────────────────────────
// The user's ask, 2026-09-16. Every card in order, every number and symbol
// on it, and the one thing to read from it. Symbols link through to the
// glossary rather than re-explaining, so there is one definition of each.
function HowTo({ P, onPick }) {
  const [open, setOpen] = useState({});
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  return (
    <div data-testid="btc15-howto">
      <div style={{ fontSize: 11.5, color: P.muted, lineHeight: 1.5, margin: "2px 0 10px" }}>
        The tab, card by card from the top. Tap a card for every number and symbol on it; tap a symbol for its full definition.
      </div>
      {HOWTO.map((sec, i) => (
        <div key={sec.id} data-testid={`btc15-howto-${sec.id}`} style={{ borderBottom: `1px solid ${P.border}` }}>
          <button type="button" data-testid={`btc15-howto-toggle-${sec.id}`} onClick={() => toggle(sec.id)}
            style={{ display: "flex", width: "100%", gap: 8, alignItems: "baseline", textAlign: "left", background: "transparent",
              border: 0, padding: "10px 0", cursor: "pointer", color: P.text, fontFamily: "inherit" }}>
            <span style={{ fontSize: 10, fontWeight: 900, color: P.accent, fontFamily: MONO, minWidth: 18 }}>{i + 1}</span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 800 }}>{sec.title}</span>
            <span aria-hidden style={{ fontSize: 15, color: P.faint, lineHeight: 1 }}>{open[sec.id] ? "−" : "+"}</span>
          </button>
          {open[sec.id] && (
            <div style={{ padding: "0 0 12px" }}>
              <TabMap spot={sec.spot} palette={P} />
              <div style={{ display: "grid", gridTemplateColumns: "minmax(88px, 34%) 1fr", gap: "6px 10px", fontSize: 11.5, lineHeight: 1.5 }}>
                {sec.items.map((it) => (
                  <div key={it.sym} style={{ display: "contents" }}>
                    <span style={{ fontFamily: MONO, fontWeight: 800, color: P.text, wordBreak: "break-word" }}>{it.sym}</span>
                    <span style={{ color: P.muted }}>
                      {it.means}
                      {it.g && (
                        <button type="button" data-testid={`btc15-howto-link-${sec.id}-${it.g}`} onClick={() => onPick(it.g)}
                          style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999, cursor: "pointer",
                            border: `1px solid ${P.border}`, background: "transparent", color: P.accent, fontFamily: "inherit", verticalAlign: "middle" }}>
                          define
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div data-testid={`btc15-howto-read-${sec.id}`} style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.55,
                color: P.text, background: `${P.accent}12`, border: `1px solid ${P.accent}44` }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", color: P.accent, display: "block", marginBottom: 3 }}>How to read it</span>
                {sec.read}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function GlossaryContent({ palette, ctx, termId, onPickTerm, initialView }) {
  const P = {
    text: "#fff", muted: "#aaa", faint: "#777", border: "#333", surface: "transparent",
    accent: "#4a9eff", green: "#22c55e", red: "#ef4444", ...(palette || {}),
  };
  const [q, setQ] = useState("");
  const [openIds, setOpenIds] = useState(() => (termId ? { [termId]: true } : {}));
  // opened on a term → the glossary; opened from the header → the guide first
  const [view, setView] = useState(initialView || (termId ? "glossary" : "howto"));
  const scrollRef = useRef(null);

  // opening the sheet on a specific term expands it and brings it into view
  useEffect(() => {
    if (!termId) return;
    setView("glossary");
    setOpenIds((o) => ({ ...o, [termId]: true }));
    setQ("");
    const el = scrollRef.current?.querySelector(`[data-testid="btc15-glossary-entry-${termId}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: "start" });
  }, [termId]);

  const hits = useMemo(() => searchGlossary(q), [q]);
  const hitIds = useMemo(() => new Set(hits.map((e) => e.id)), [hits]);
  const toggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));
  const pick = (id) => {
    setQ("");
    setView("glossary");
    setOpenIds((o) => ({ ...o, [id]: true }));
    onPickTerm?.(id);
    const el = scrollRef.current?.querySelector(`[data-testid="btc15-glossary-entry-${id}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: "start" });
  };

  const seg = (key, label) => (
    <button key={key} type="button" data-testid={`btc15-explain-view-${key}`} onClick={() => setView(key)}
      aria-pressed={view === key}
      style={{ flex: 1, padding: "7px 0", fontSize: 10.5, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", cursor: "pointer",
        fontFamily: "inherit", border: `1px solid ${view === key ? P.accent : P.border}`, borderRadius: 8,
        background: view === key ? `${P.accent}22` : "transparent", color: view === key ? P.accent : P.muted }}>
      {label}
    </button>
  );
  return (
    <div ref={scrollRef} data-testid="btc15-glossary">
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>{seg("howto", "How to use")}{seg("glossary", "Glossary")}</div>
      {view === "howto" && <HowTo P={P} onPick={pick} />}
      {view === "glossary" && (<>
      <input
        data-testid="btc15-glossary-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search — fee, calibrated, why they lose…"
        style={{
          width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 10, marginBottom: 10,
          border: `1px solid ${P.border}`, background: P.surface, color: P.text, fontSize: 13, fontFamily: "inherit",
        }}
      />
      {hits.length === 0 && (
        <div data-testid="btc15-glossary-empty" style={{ fontSize: 12.5, color: P.muted, padding: "10px 0" }}>
          Nothing matches “{q}”.
        </div>
      )}
      {GROUPS.map(([key, label]) => {
        const rows = inGroup(key).filter((e) => hitIds.has(e.id));
        if (!rows.length) return null;
        return (
          <div key={key} data-testid={`btc15-glossary-group-${key}`} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.6, textTransform: "uppercase", color: P.muted, margin: "10px 0 2px" }}>
              {label}
            </div>
            {rows.map((e) => (
              <Entry key={e.id} entry={e} palette={P} ctx={ctx} open={!!openIds[e.id]} onToggle={() => toggle(e.id)} onPick={pick} />
            ))}
          </div>
        );
      })}
      <div style={{ fontSize: 10.5, color: P.faint, lineHeight: 1.5, padding: "10px 0 0" }}>
        Every formula here mirrors the desk's own code. Nothing on this tab forecasts Bitcoin — the market is a coin
        flip priced to the cent, and twelve signals were tested against it without one beating it.
      </div>
      </>)}
    </div>
  );
}
